import { useCallback, useMemo } from 'react';

import { useTheme } from '@mui/material';

import useRegularOptions from '@/components/Chart/hooks/options/useRegularOptions';
import getRegularTooltip from '@/components/Chart/hooks/tooltip/getRegularTooltip';
import useForwardCurveTooltipFormatter from '@/components/Chart/hooks/tooltip/useForwardCurveTooltipFormatter';
import type {
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/components/Chart/types/highcharts';
import { regularChartPalette } from '@/constants/constants';
import type { Color } from '@/features/Dashboard/form/AppForm/components/ColorPaletteSelector/const';
import {
  balancedPalette,
  monoBluePalette,
  vibrantPalette
} from '@/features/Dashboard/form/AppForm/components/ColorPaletteSelector/const';

import type { XAxisOptions } from 'highcharts';

/**
 * Available color palettes for forward curve charts.
 * Order matters as it determines priority when matching colors.
 */
const PALETTES: readonly Color[][] = [vibrantPalette, monoBluePalette, balancedPalette] as const;

/**
 * Finds the palette that contains the given color.
 * @param color - Color hex string to search for (case-insensitive)
 * @returns The matching palette array or null if no match found
 */
const findPaletteForColor = (color: string | undefined): readonly Color[] | null => {
  if (!color || typeof color !== 'string') {
    return null;
  }

  const normalizedColor = color.toUpperCase();
  return (
    PALETTES.find(palette =>
      palette.some(paletteColor => paletteColor.hex.toUpperCase() === normalizedColor)
    ) ?? null
  );
};

/**
 * Determines the color for a series based on its current color and combinedIndex.
 * @param currentColor - Current color of the series (may be undefined)
 * @param combinedIndex - Index of the series in the combined chart
 * @returns Hex color string
 */
const getSeriesColor = (currentColor: string | undefined, combinedIndex: number): string => {
  const palette = findPaletteForColor(currentColor);

  if (palette && combinedIndex >= 0) {
    return palette[combinedIndex % palette.length].hex;
  }

  // Fallback to regular chart palette
  // Handle negative indices by using modulo with length adjustment
  const paletteLength = regularChartPalette.length;
  const normalizedIndex =
    combinedIndex < 0
      ? ((combinedIndex % paletteLength) + paletteLength) % paletteLength
      : combinedIndex;
  return regularChartPalette[normalizedIndex % paletteLength];
};

/**
 * Assigns color to a series based on its palette and index.
 * Note: Mutates the series object as Highcharts expects mutable series options.
 * @param series - Series options to assign color to
 */
const assignSeriesColor = (series: ExtendedSeriesOptionsType): void => {
  const { combinedIndex } = series.custom;
  const currentColor = typeof series.color === 'string' ? series.color : undefined;
  series.color = getSeriesColor(currentColor, combinedIndex);
};

/**
 * Configures X-axis options for forward curve charts.
 * Note: Mutates the xAxisOptions object as Highcharts expects mutable options.
 * @param xAxisOptions - X-axis options to configure
 */
const configureXAxis = (xAxisOptions: XAxisOptions): void => {
  xAxisOptions.type = 'linear';
  xAxisOptions.tickInterval = 1;
  xAxisOptions.crosshair = true;

  // Remove formatter if present to use default behavior
  if (xAxisOptions.labels?.formatter) {
    xAxisOptions.labels.formatter = undefined;
  }
};

/**
 * Hook for creating Highcharts options for forward curve charts.
 * @param height - Optional chart height (multiplied by row height in base options)
 * @param preserveColors
 * @returns Object with createOptions function
 */
const useForwardCurveOptions = (height?: number, preserveColors?: boolean) => {
  const { createOptions: createRegularOptions } = useRegularOptions(height);
  const { createTooltipFormatter } = useForwardCurveTooltipFormatter();
  const theme = useTheme();

  // Memoize tooltip to avoid recreating it on every render
  const tooltip = useMemo(
    () => getRegularTooltip(createTooltipFormatter, theme).tooltip,
    [createTooltipFormatter, theme]
  );

  const createOptions = useCallback(
    (chartSeries: ExtendedSeriesOptionsType[], hiddenSeriesIds: string[] = []): ExtendedOptions => {
      // Assign colors to series (mutates series objects as required by Highcharts)
      if (!preserveColors) chartSeries.forEach(assignSeriesColor);

      const baseOptions = createRegularOptions(chartSeries, hiddenSeriesIds);

      const options: ExtendedOptions = {
        ...baseOptions,
        navigator: { enabled: false },
        rangeSelector: { enabled: false }
      };

      // Configure X-axis if present
      const xAxisOptions = options.xAxis as XAxisOptions | undefined;
      if (xAxisOptions) {
        configureXAxis(xAxisOptions);
      }

      return {
        ...options,
        xAxis: xAxisOptions,
        tooltip,
        chart: {
          ...options.chart,
          marginTop: 40,
          zooming: { type: undefined }
        },
        plotOptions: {
          ...options.plotOptions,
          series: {
            ...options.plotOptions?.series,
            dataLabels: {
              enabled: false
            }
          }
        }
      };
    },
    [createRegularOptions, tooltip]
  );

  return { createOptions };
};

export default useForwardCurveOptions;
